import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowLeft, Save, Eye, FileEdit, Loader2, AlertTriangle, CheckCircle2, Upload, Image as ImageIcon, X } from "lucide-react";
import SEO from "../components/SEO";
import { API } from "../apiBase";
const REMARK_PLUGINS = [remarkGfm];

const EMPTY = {
  title: "", slug: "", excerpt: "", cover_image: "", content: "",
  author: "MetaConnect", tags: [], status: "draft",
};

export default function AdminEditor() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const [post, setPost] = useState(EMPTY);
  const [tagsInput, setTagsInput] = useState("");
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");
  const [view, setView] = useState("edit"); // edit | preview
  const [uploading, setUploading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState("");

  const uploadImage = async (file, { intoContent = false } = {}) => {
    if (!file) return null;
    if (!file.type || !file.type.startsWith("image/")) {
      setErr("Le fichier sélectionné n'est pas une image.");
      return null;
    }
    if (file.size > 5 * 1024 * 1024) {
      setErr("Image trop volumineuse (max 5 Mo).");
      return null;
    }
    setUploading(true);
    setErr("");
    setUploadMsg("");
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await axios.post(`${API}/admin/blog/upload-image`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = data.url;
      if (intoContent) {
        // Insert at end of current content as Markdown image
        setPost((p) => ({
          ...p,
          content: (p.content || "") + `\n\n![${file.name.replace(/\.[^/.]+$/, "")}](${url})\n\n`,
        }));
        setUploadMsg(`✅ Image insérée dans le contenu (${Math.round(data.size / 1024)} Ko)`);
      } else {
        setPost((p) => ({ ...p, cover_image: url }));
        setUploadMsg(`✅ Image de couverture mise à jour (${Math.round(data.size / 1024)} Ko)`);
      }
      setTimeout(() => setUploadMsg(""), 5000);
      return url;
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Échec de l'upload.";
      setErr(`Upload échoué : ${detail}`);
      return null;
    } finally {
      setUploading(false);
    }
  };

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/blog/posts/${id}`);
        setPost({ ...EMPTY, ...data, tags: data.tags || [] });
        setTagsInput((data.tags || []).join(", "));
      } catch (e) {
        setErr("Impossible de charger l'article.");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, isEdit]);

  const update = (k) => (e) => setPost({ ...post, [k]: e.target.value });

  const onSubmit = async (e, statusOverride) => {
    e.preventDefault();
    setErr(""); setOk(""); setSaving(true);
    try {
      const tags = tagsInput.split(",").map((t) => t.trim()).filter(Boolean);
      const payload = { ...post, tags, status: statusOverride || post.status };
      if (!payload.slug) delete payload.slug; // let backend generate

      let res;
      if (isEdit) {
        res = await axios.put(`${API}/admin/blog/posts/${id}`, payload);
      } else {
        res = await axios.post(`${API}/admin/blog/posts`, payload);
      }
      setPost({ ...post, ...res.data });
      setOk(isEdit ? "Article mis à jour." : "Article créé.");
      if (!isEdit) navigate(`/admin/edit/${res.data.id}`, { replace: true });
    } catch (e) {
      const d = e?.response?.data?.detail;
      setErr(typeof d === "string" ? d : "Erreur lors de l'enregistrement.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--mc-navy-950)] grid place-items-center pt-24">
        <Loader2 className="animate-spin text-[#0284C7]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--mc-navy-950)] hex-grid pt-28 pb-16 px-5 lg:px-8" data-testid="page-admin-editor">
      <SEO title={isEdit ? "Éditer un article" : "Nouvel article"} description="Éditeur admin." canonical={isEdit ? `/admin/edit/${id}` : "/admin/new"} noIndex />

      <div className="max-w-5xl mx-auto">
        <Link to="/admin" className="inline-flex items-center gap-2 text-[13px] text-[var(--mc-muted)] hover:text-[#0F2B5B] transition mb-6">
          <ArrowLeft size={14} /> Retour aux articles
        </Link>

        <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-[#0284C7]">{isEdit ? "Édition" : "Nouveau"}</div>
            <h1 className="font-display text-[#0F2B5B] text-3xl mt-1">{isEdit ? "Éditer l'article" : "Rédiger un article"}</h1>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setView("edit")}
                    className={`px-4 py-2 rounded-full text-[12px] inline-flex items-center gap-1.5 transition ${view === "edit" ? "btn-primary" : "btn-ghost"}`}>
              <FileEdit size={14} /> Éditer
            </button>
            <button onClick={() => setView("preview")} data-testid="admin-preview-toggle"
                    className={`px-4 py-2 rounded-full text-[12px] inline-flex items-center gap-1.5 transition ${view === "preview" ? "btn-primary" : "btn-ghost"}`}>
              <Eye size={14} /> Aperçu
            </button>
          </div>
        </div>

        {view === "edit" ? (
          <form onSubmit={onSubmit} data-testid="admin-editor-form" className="grid gap-5">
            <div className="grid gap-5 lg:grid-cols-3">
              <div className="lg:col-span-2 grid gap-5">
                <Field label="Titre *">
                  <input className="mc-input" required minLength={2} value={post.title}
                         onChange={update("title")} data-testid="admin-input-title"
                         placeholder="Ex: Comment intégrer Pro Santé Connect en 2026" />
                </Field>
                <Field label="Slug (URL) — laissez vide pour auto-générer">
                  <input className="mc-input" value={post.slug || ""}
                         onChange={update("slug")} data-testid="admin-input-slug"
                         placeholder="comment-integrer-pro-sante-connect" />
                </Field>
                <Field label="Résumé / extrait (≤ 400 caractères)">
                  <textarea className="mc-input min-h-[80px]" maxLength={400}
                            value={post.excerpt || ""} onChange={update("excerpt")}
                            data-testid="admin-input-excerpt"
                            placeholder="Une courte description qui apparaît dans la liste et le SEO." />
                </Field>

                <Field label="Contenu (Markdown)">
                  <textarea
                    className="mc-input min-h-[400px] font-mono text-[13px] leading-relaxed"
                    required value={post.content} onChange={update("content")}
                    data-testid="admin-input-content"
                    placeholder={"# Titre H1\n\nVotre **paragraphe** en _Markdown_.\n\n## Sous-titre\n\n- Liste\n- Avec des points\n\n[Lien](https://metaconnect.fr)"} />
                  <div className="mt-2 flex items-center gap-2 flex-wrap">
                    <label
                      data-testid="admin-content-image-upload"
                      className={`inline-flex items-center gap-1.5 text-[12px] px-3 py-1.5 rounded-full border cursor-pointer transition
                        ${uploading
                          ? "bg-[rgba(127,179,255,0.05)] border-[rgba(127,179,255,0.15)] text-[var(--mc-muted)] cursor-wait"
                          : "bg-[rgba(127,179,255,0.08)] border-[rgba(127,179,255,0.25)] text-[#0284C7] hover:bg-[rgba(127,179,255,0.14)] hover:text-[#0F2B5B]"}`}
                    >
                      {uploading ? <Loader2 size={13} className="animate-spin" /> : <ImageIcon size={13} />}
                      {uploading ? "Envoi…" : "Insérer une image"}
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp,image/gif,image/avif"
                        disabled={uploading}
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (f) uploadImage(f, { intoContent: true });
                          e.target.value = "";
                        }}
                        className="hidden"
                      />
                    </label>
                    <span className="text-[11px] text-[var(--mc-muted)]">
                      L'image sera ajoutée à la fin du contenu en Markdown
                    </span>
                  </div>
                  <p className="mt-2 text-[11px] text-[var(--mc-muted)]">
                    Astuce : <code className="text-[#0284C7]">## Titre</code>, <code className="text-[#0284C7]">**gras**</code>, <code className="text-[#0284C7]">- liste</code>, <code className="text-[#0284C7]">[lien](url)</code>, <code className="text-[#0284C7]">![alt](img.jpg)</code>
                  </p>
                </Field>
              </div>

              <aside className="grid gap-5 content-start">
                <Field label="Statut">
                  <select className="mc-input" value={post.status} onChange={update("status")}
                          data-testid="admin-input-status">
                    <option value="draft">Brouillon</option>
                    <option value="published">Publié</option>
                  </select>
                </Field>
                <Field label="Auteur">
                  <input className="mc-input" value={post.author} onChange={update("author")} />
                </Field>
                <Field label="Image de couverture">
                  <div className="flex flex-col gap-2">
                    <input className="mc-input" value={post.cover_image || ""}
                           onChange={update("cover_image")}
                           data-testid="admin-input-cover"
                           placeholder="https://… ou /api/blog/images/uuid.jpg" />
                    {post.cover_image && (
                      <div className="rounded-lg border border-[rgba(127,179,255,0.15)] bg-[rgba(127,179,255,0.03)] p-3 flex items-center gap-3">
                        <img
                          src={post.cover_image}
                          alt="Aperçu couverture"
                          className="w-20 h-14 object-cover rounded border border-[rgba(127,179,255,0.15)]"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-[12px] text-[#0F2B5B] truncate font-mono">{post.cover_image}</div>
                          <div className="text-[11px] text-[var(--mc-muted)]">Image actuelle</div>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setPost((p) => ({ ...p, cover_image: "" }));
                            setUploadMsg("🗑️ Image de couverture supprimée (non publiée tant que vous n'avez pas enregistré)");
                            setTimeout(() => setUploadMsg(""), 5000);
                          }}
                          data-testid="admin-cover-remove"
                          title="Retirer l'image de couverture"
                          className="shrink-0 inline-flex items-center gap-1.5 text-[12px] px-3 py-1.5 rounded-full border border-[rgba(255,120,120,0.3)] bg-[rgba(255,120,120,0.06)] text-[#DC2626] hover:bg-[rgba(255,120,120,0.12)] transition"
                        >
                          <X size={13} /> Retirer
                        </button>
                      </div>
                    )}
                    <div className="flex items-center gap-2 flex-wrap">
                      <label
                        data-testid="admin-cover-upload"
                        className={`inline-flex items-center gap-1.5 text-[12px] px-3 py-1.5 rounded-full border cursor-pointer transition
                          ${uploading
                            ? "bg-[rgba(127,179,255,0.05)] border-[rgba(127,179,255,0.15)] text-[var(--mc-muted)] cursor-wait"
                            : "bg-[rgba(127,179,255,0.08)] border-[rgba(127,179,255,0.25)] text-[#0284C7] hover:bg-[rgba(127,179,255,0.14)] hover:text-[#0F2B5B]"}`}
                      >
                        {uploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
                        {uploading ? "Envoi…" : (post.cover_image ? "Remplacer l'image" : "Uploader une image")}
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/webp,image/gif,image/avif"
                          disabled={uploading}
                          onChange={(e) => {
                            const f = e.target.files?.[0];
                            if (f) uploadImage(f, { intoContent: false });
                            e.target.value = "";
                          }}
                          className="hidden"
                        />
                      </label>
                      <span className="text-[11px] text-[var(--mc-muted)]">JPEG · PNG · WebP · max 5 Mo</span>
                    </div>
                    {uploadMsg && (
                      <div className="text-[12px] text-[var(--mc-mint)]" data-testid="admin-upload-msg">{uploadMsg}</div>
                    )}
                  </div>
                </Field>
                <Field label="Tags (séparés par des virgules)">
                  <input className="mc-input" value={tagsInput}
                         onChange={(e) => setTagsInput(e.target.value)}
                         data-testid="admin-input-tags"
                         placeholder="e-santé, MSSanté, conformité" />
                </Field>

                <div className="grid gap-3 mt-2">
                  <button type="submit" disabled={saving} data-testid="admin-save-btn"
                          className="btn-primary px-5 py-3 rounded-full text-[13px] font-medium inline-flex items-center justify-center gap-2 disabled:opacity-60">
                    {saving && <Loader2 size={14} className="animate-spin" />}
                    <Save size={14} /> Enregistrer ({post.status === "published" ? "Publié" : "Brouillon"})
                  </button>
                  {post.status === "draft" && (
                    <button type="button" onClick={(e) => onSubmit(e, "published")}
                            disabled={saving} data-testid="admin-publish-btn"
                            className="btn-ghost px-5 py-3 rounded-full text-[13px] font-medium inline-flex items-center justify-center gap-2 disabled:opacity-60">
                      Publier maintenant
                    </button>
                  )}
                </div>

                {ok && (
                  <div className="flex items-start gap-2 rounded-lg border border-[rgba(110,231,197,0.4)] bg-[rgba(110,231,197,0.08)] px-4 py-3 text-[12px] text-[var(--mc-mint)]">
                    <CheckCircle2 size={14} className="mt-0.5" /><span>{ok}</span>
                  </div>
                )}
                {err && (
                  <div className="flex items-start gap-2 rounded-lg border border-[rgba(255,120,120,0.35)] bg-[rgba(255,120,120,0.08)] px-4 py-3 text-[12px] text-[#DC2626]">
                    <AlertTriangle size={14} className="mt-0.5" /><span>{err}</span>
                  </div>
                )}
              </aside>
            </div>
          </form>
        ) : (
          <div className="mc-card p-8 lg:p-12">
            {post.cover_image && (
              <img src={post.cover_image} alt="" className="w-full max-h-[360px] object-cover rounded-lg mb-8" />
            )}
            <div className="text-[11px] uppercase tracking-[0.22em] text-[#0284C7]">{post.tags?.join(" · ") || "Article"}</div>
            <h1 className="font-display text-[#0F2B5B] text-4xl mt-3">{post.title || "Sans titre"}</h1>
            <p className="mt-3 text-[15px] text-[var(--mc-muted)] leading-relaxed">{post.excerpt}</p>
            <div className="mt-8 prose-mc">
              <ReactMarkdown remarkPlugins={REMARK_PLUGINS}>{post.content || ""}</ReactMarkdown>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[12px] uppercase tracking-[0.16em] text-[#0284C7] mb-2">{label}</span>
      {children}
    </label>
  );
}
